import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, clearToken, getToken, API } from "../lib/api";
import { BrandMark } from "../components/BrandMark";
import { SECTIONS } from "../lib/checklistSchema";
import { Button } from "../components/ui/button";
import { ArrowLeft, Download, Mail } from "lucide-react";
import { toast } from "sonner";

const formatValue = (v) => {
  if (v === undefined || v === null || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

export default function AdminSubmission() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/admin/submissions/${id}`);
        setSub(data);
      } catch {
        clearToken();
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const downloadPDF = () => {
    const token = getToken();
    const url = `${API}/admin/submissions/${id}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  const resendEmail = async () => {
    setEmailing(true);
    try {
      await api.post(`/admin/submissions/${id}/resend-email`);
      toast.success("Email queued for delivery.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Email failed.";
      toast.error(msg);
    } finally {
      setEmailing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] p-10 text-[#737373]" data-testid="submission-loading">
        Loading…
      </div>
    );
  }
  if (!sub) return null;

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="submission-detail-page">
      <header className="border-b border-[#E5E5E0] bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <BrandMark size={32} />
          <Link
            to="/admin"
            className="text-[12px] uppercase tracking-[0.2em] text-[#737373] hover:text-[#010082] flex items-center gap-2"
            data-testid="back-to-dashboard-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#010082]">
              Submission
            </div>
            <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1A1A1A] mt-1 tracking-tight">
              {sub.data.seller_name || "Untitled Seller"}
            </h1>
            <p className="text-[13px] text-[#737373] mt-2">
              {sub.data.property_address}
              {sub.data.city_state_zip ? `, ${sub.data.city_state_zip}` : ""} ·{" "}
              {new Date(sub.submitted_at).toLocaleString()}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={resendEmail}
              disabled={emailing}
              variant="outline"
              className="h-11 border-[#010082]/30 text-[#010082] hover:bg-[#010082]/[0.04] bg-transparent"
              data-testid="resend-email-button"
            >
              <Mail className="w-4 h-4 mr-2" strokeWidth={1.75} />
              {emailing ? "Sending…" : "Email to Louis"}
            </Button>
            <Button
              onClick={downloadPDF}
              className="h-11 bg-[#010082] hover:bg-[#010082]/90 text-white"
              data-testid="download-pdf-button"
            >
              <Download className="w-4 h-4 mr-2" strokeWidth={1.75} />
              Download PDF
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {SECTIONS.map((section, i) => (
            <div
              key={section.id}
              className="bg-white border border-[#E5E5E0] rounded-xl p-8"
              data-testid={`detail-section-${section.id}`}
            >
              <div className="flex items-baseline gap-3">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#010082]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h2 className="font-serif-display text-2xl text-[#1A1A1A] tracking-tight">
                  {section.title}
                </h2>
              </div>
              <dl className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                {section.fields.map((f) => (
                  <div key={f.key} data-testid={`detail-field-${f.key.replace(/_/g, "-")}`}>
                    <dt className="text-[11px] uppercase tracking-[0.18em] text-[#737373]">
                      {f.label}
                    </dt>
                    <dd className="text-[14px] text-[#1A1A1A] mt-1.5 leading-relaxed whitespace-pre-wrap">
                      {formatValue(sub.data[f.key])}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
